// src/components/AddPackageModal.js
// Updated: 10/28/2025 03:44 PM EDT (Refined error handling)
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useCallback } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { X, UploadCloud } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

export default function AddPackageModal({ isOpen, onClose, session }) {
  const [packageName, setPackageName] = useState('');
  const [packageDescription, setPackageDescription] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser, userData } = useAuth();

  const onDrop = useCallback((acceptedFiles) => {
    const droppedFile = acceptedFiles?.[0];
    if (!droppedFile) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(droppedFile.type)) {
      setError('Invalid file type. Please upload a PDF, JPG, or PNG.');
      return;
    }
    setFile(droppedFile);
    setError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isLoading,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!packageName.trim()) return setError('Package name is required.');
    if (!session?.id) return setError('Session ID is missing. Cannot add package.');
    if (!file) return setError('A document file must be uploaded to create a package.');
    if (!currentUser?.uid) return setError('You must be signed in to upload.');
    if (!userData?.orgId) return setError('Your profile is missing an organization. Contact an admin.');

    setIsLoading(true);
    setProgress(0);
    setError('');

    let uploadTaskSnapshot = null; // To store snapshot reference

    try {
      const fileId = uuidv4();
      const storagePath = `organizations/${userData.orgId}/scoringSessions/${session.id}/packages/${fileId}/${file.name}`;

      const metadata = {
        contentType: file.type || 'application/octet-stream',
        customMetadata: {
          orgId: userData.orgId,
          uploadedBy: currentUser.uid,
          sessionId: session.id,
          packageName: packageName,
        },
      };

      const ref = storageRef(storage, storagePath);
      console.log('[AddPackageModal] PRE-UPLOAD CHECK:', { // Keep this log
        currentUserUid: currentUser?.uid,
        userDataOrgId: userData?.orgId,
        storagePath: storagePath,
        metadataToSend: metadata,
      });

      const uploadTask = uploadBytesResumable(ref, file, metadata);

      // --- MODIFIED PROMISE HANDLING ---
      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Progress reporting (as before)
            const pct =
              snapshot.totalBytes > 0
                ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                : 0;
            setProgress(pct);
          },
          (err) => {
            // Log the specific error from the task listener
            console.error('[AddPackageModal] Upload task error event triggered:', err);
            // Reject with the specific error object
            reject(err); // Pass the original FirebaseError
          },
          () => {
            // Completion handler
            console.log('[AddPackageModal] Upload task completed successfully.');
            uploadTaskSnapshot = uploadTask.snapshot; // Store snapshot on success
            resolve();
          }
        );
      });
      // --- END MODIFIED PROMISE HANDLING ---

      // --- MOVED FIRESTORE LOGIC OUTSIDE INITIAL TRY, AFTER SUCCESSFUL UPLOAD ---
      // If the promise above resolved, the upload *should* be complete.

      let downloadURL = null;
      try {
        console.log('[AddPackageModal] Attempting to get download URL...');
        downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
        console.log('[AddPackageModal] Successfully got download URL:', downloadURL);
      } catch (getUrlError) {
        console.error('[AddPackageModal] Error getting download URL:', getUrlError);
        // Even if we can't get URL, file might be uploaded. Log warning, maybe proceed cautiously?
        setError(`Upload likely succeeded, but failed to get download URL: ${getUrlError.message}. Firestore documents not created.`);
        // Stop execution here if URL is critical
        setIsLoading(false);
        setProgress(0);
        return; // Exit handleSubmit
      }

      // --- Wrap Firestore operations in their own try...catch ---
      try {
        console.log('[AddPackageModal] Attempting to create Firestore documents...');
        // Create a global "documents" record
        const documentRecord = await addDoc(collection(db, 'documents'), {
          orgId: userData.orgId,
          uploaderId: currentUser.uid,
          uploaderName: currentUser.displayName || '',
          fileName: file.name,
          fileType: file.type,
          storagePath,
          downloadURL, // Use the obtained URL
          uploadedAt: serverTimestamp(),
          context: 'scoring',
          linkedSessionId: session.id,
          linkedSessionName: session.name || '',
          linkedPackageName: packageName,
        });
        console.log('[AddPackageModal] Created /documents record:', documentRecord.id);

        // Create the package record under this session
        await addDoc(collection(db, 'scoringSessions', session.id, 'packages'), {
          name: packageName,
          description: packageDescription || '',
          linkedDocumentId: documentRecord.id,
          addedBy: currentUser.uid,
          addedAt: serverTimestamp(),
        });
        console.log('[AddPackageModal] Created /scoringSessions/.../packages record.');

        // Success - Reset state and close modal
        setPackageName('');
        setPackageDescription('');
        setFile(null);
        onClose();

      } catch (firestoreError) {
          console.error('[AddPackageModal] Error creating Firestore documents:', firestoreError);
          // If Firestore fails, the file is already in Storage. Inform the user.
          setError(`Upload succeeded, but failed to save document records: ${firestoreError.message}. Please try adding the package again or contact support.`);
      }

    } catch (uploadError) {
      // This catch block now *only* catches errors from the uploadTask promise
      console.error('[AddPackageModal] Upload failed:', uploadError); // Log the specific error
      // Use the code property for a potentially clearer message
      const errorCode = uploadError.code || 'unknown';
      setError(`Upload failed (${errorCode}): ${uploadError.message}.`);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  if (!isOpen) return null;

  // --- JSX remains the same ---
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
     <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
       <button
         onClick={onClose}
         disabled={isLoading}
         className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
       >
         <X className="h-6 w-6" />
       </button>

       <h2 className="text-xl font-semibold text-gray-900">Add Package & Upload Document</h2>

       <form onSubmit={handleSubmit} className="mt-4 space-y-4">
         <div>
           <label htmlFor="package-name" className="block text-sm font-medium text-gray-700">
             Package Name (e.g., Proposal A)
           </label>
           <input
             type="text"
             id="package-name"
             value={packageName}
             onChange={(e) => setPackageName(e.target.value)}
             className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
             required
           />
         </div>

         <div>
           <label htmlFor="package-description" className="block text-sm font-medium text-gray-700">
             Description (optional)
           </label>
           <textarea
             id="package-description"
             rows="3"
             value={packageDescription}
             onChange={(e) => setPackageDescription(e.target.value)}
             className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
           />
         </div>

         <div className="pt-2">
           <label className="mb-2 block text-sm font-medium text-gray-700">
             Primary Document File
           </label>
           <div
             {...getRootProps()}
             className={`flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
               isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
             }`}
           >
             <input {...getInputProps()} />
             {file ? (
               <p className="text-sm font-medium text-green-600">File Selected: {file.name}</p>
             ) : (
               <>
                 <UploadCloud className="h-8 w-8 text-gray-400" />
                 <p className="mt-1 text-sm text-gray-500">
                   Drag & drop PDF/Image here, or click to select
                 </p>
               </>
             )}
           </div>
         </div>

         {isLoading && (
           <div className="mt-4">
             <p className="text-sm font-medium text-gray-700">
               Uploading... {Math.round(progress)}%
             </p>
             <div className="mt-1 w-full rounded-full bg-gray-200">
               <div
                 className="rounded-full bg-blue-600 p-0.5 text-center text-xs font-medium leading-none text-blue-100"
                 style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
               >
                 &nbsp;
               </div>
             </div>
           </div>
         )}

         {error && <p className="text-sm text-red-600">{error}</p>}

         <div className="flex justify-end space-x-3 pt-4">
           <button
             type="button"
             onClick={onClose}
             disabled={isLoading}
             className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
           >
             Cancel
           </button>
           <button
             type="submit"
             disabled={isLoading || !file}
             className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
           >
             {isLoading ? 'Processing...' : 'Create & Upload'}
           </button>
         </div>
       </form>
     </div>
   </div>
  );
}